import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { fetchAuthSession, fetchUserAttributes, getCurrentUser, signOut } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import axios from 'axios'

const AuthContext = createContext({})

const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [userAttributes, setUserAttributes] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [userRole, setUserRole] = useState(null)       // 'SuperAdmins' | 'Admins' | 'Users'
    const [userModules, setUserModules] = useState([])   // ['conciliacion_bancaria', 'comparador_dian']
    const [modulesLoaded, setModulesLoaded] = useState(false)

    /**
     * Carga los módulos del usuario desde la Lambda Admin.
     * Sólo se llama cuando el usuario ya está autenticado.
     */
    const loadUserModules = useCallback(async (idToken) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/me/modulos`, {
                headers: { Authorization: `Bearer ${idToken}` }
            })
            setUserModules(res.data?.modulos || [])
        } catch (err) {
            console.warn('No se pudieron cargar los módulos del usuario:', err)
            setUserModules([])
        } finally {
            setModulesLoaded(true)
        }
    }, [])

    const refreshUserModules = useCallback(async () => {
        try {
            setModulesLoaded(false)
            const session = await fetchAuthSession()
            if (!session.tokens) {
                setUserModules([])
                setModulesLoaded(true)
                return
            }
            await loadUserModules(session.tokens.idToken.toString())
        } catch (err) {
            console.warn('Error refrescando módulos:', err)
            setUserModules([])
            setModulesLoaded(true)
        }
    }, [loadUserModules])

    const loadUserData = useCallback(async () => {
        try {
            setIsLoading(true)

            const session = await fetchAuthSession()

            if (session.tokens) {
                const currentUser = await getCurrentUser()
                const attributes = await fetchUserAttributes()

                // Extraer grupos del JWT — el usuario puede tener múltiples grupos
                const payload = session.tokens.idToken.payload
                const groups = payload['cognito:groups'] || []

                // Determinar rol primario: prioridad SuperAdmins > Admins > Users
                let role = 'Users'
                if (groups.includes('SuperAdmins')) role = 'SuperAdmins'
                else if (groups.includes('Admins')) role = 'Admins'

                setUser(currentUser)
                setUserAttributes(attributes)
                setIsAuthenticated(true)
                setUserRole(role)

                // Cargar módulos del usuario de forma INDEPENDIENTE
                // Si falla la API de módulos, NO debe afectar el estado de autenticación
                const idToken = session.tokens.idToken.toString()
                loadUserModules(idToken).catch(err => {
                    console.warn('Error cargando módulos (no crítico):', err)
                    setModulesLoaded(true)
                })
            } else {
                resetState()
            }
        } catch (error) {
            console.error('Error loading user data:', error)
            resetState()
        } finally {
            setIsLoading(false)
        }
    }, [loadUserModules])

    const resetState = () => {
        setUser(null)
        setUserAttributes(null)
        setIsAuthenticated(false)
        setUserRole(null)
        setUserModules([])
        setModulesLoaded(false)
    }

    useEffect(() => {
        loadUserData()

        const hubListener = Hub.listen('auth', ({ payload }) => {
            const { event } = payload
            switch (event) {
                case 'signedIn':
                    loadUserData()
                    break
                case 'signedOut':
                    resetState()
                    break
                case 'tokenRefresh':
                    loadUserData()
                    break
                default:
                    break
            }
        })

        return () => hubListener()
    }, [loadUserData])

    const handleSignOut = async () => {
        try {
            await signOut()
            resetState()
        } catch (error) {
            console.error('Error signing out:', error)
        }
    }

    /**
     * Verifica si el usuario tiene acceso a un módulo específico.
     * SuperAdmins siempre tienen acceso a todo.
     */
    const hasModule = (moduleKey) => {
        if (userRole === 'SuperAdmins') return true
        return userModules.includes(moduleKey)
    }

    const value = {
        user,
        userAttributes,
        isLoading,
        isAuthenticated,
        userRole,
        userModules,
        modulesLoaded,
        signOut: handleSignOut,
        refreshUserData: loadUserData,
        refreshUserModules,
        hasModule,
        // Helpers computados
        userName:       userAttributes?.name || userAttributes?.email || 'Usuario',
        userEmail:      userAttributes?.email || '',
        userEmpresa:    userAttributes?.['custom:Empresa'] || 'Sin empresa',
        isSuperAdmin:   userRole === 'SuperAdmins',
        isAdmin:        userRole === 'Admins' || userRole === 'SuperAdmins',
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider')
    }
    return context
}
